import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { fmtINR, longDate } from "../lib/format";
import { downloadCSV } from "../lib/csv";
import { exportPDF } from "../lib/pdf";
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Wallet,
  Landmark,
  Scale,
  Banknote,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeftRight,
  ChevronDown,
  ChevronUp,
  X,
  FileText,
  Download,
  Layers,
  Shield,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Wand2,
} from "lucide-react";
import { DriftDrillButton } from "../components/DriftBanner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const todayISO = () => new Date().toISOString().slice(0, 10);

function StatCard({ label, value, accent, icon: Icon, testId }) {
  const tones = {
    emerald: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    rose: "text-rose-400 border-rose-500/30 bg-rose-500/10",
    sky: "text-sky-400 border-sky-500/30 bg-sky-500/10",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  };
  return (
    <div
      data-testid={testId}
      className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-4 md:p-5 min-w-0 overflow-hidden transition-transform hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono truncate">
          {label}
        </div>
        <div
          className={`shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center ${tones[accent]}`}
        >
          <Icon size={14} />
        </div>
      </div>
      <div
        className="text-xl md:text-2xl lg:text-3xl font-display text-white mt-3 tracking-tight tabular-nums whitespace-nowrap truncate"
        title={fmtINR(value, { decimals: 2 })}
      >
        {fmtINR(value, { compact: true })}
      </div>
      <div className="text-[11px] text-slate-500 mt-1 truncate tabular-nums font-mono">
        {fmtINR(value, { decimals: 2 })}
      </div>
    </div>
  );
}

// Formats either a ₹ amount or a unit count (no symbol), depending on `unit`.
function fmtReconValue(v, unit) {
  if (unit === "UNITS") {
    return Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  }
  return fmtINR(v, { decimals: 2 });
}

function ReconciliationPanel({ reconciliations, allBalanced }) {
  return (
    <div
      data-testid="ledger-recon-panel"
      className="mt-5 holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl overflow-hidden"
    >
      <div className="px-5 py-4 flex items-center justify-between flex-wrap gap-3 border-b border-slate-800/80">
        <div className="flex items-center gap-3">
          <ArrowLeftRight size={16} className="text-sky-300" />
          <div>
            <div className="text-sm font-semibold text-white">Assets ↔ Liabilities reconciliation</div>
            <div className="text-[11px] text-slate-500 font-mono mt-0.5">
              Structural checks that the books are internally consistent
            </div>
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-mono uppercase tracking-wider ${
            allBalanced
              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
              : "bg-rose-500/10 text-rose-300 border-rose-500/30"
          }`}
          data-testid="ledger-recon-overall"
        >
          {allBalanced ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
          {allBalanced ? "All balanced" : "Drift detected"}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-800/80">
        {reconciliations.map((r) => (
          <ReconciliationRow key={r.id} recon={r} />
        ))}
      </div>
    </div>
  );
}

function ReconciliationRow({ recon }) {
  const balanced = recon.balanced;
  const driftFmt = fmtReconValue(Math.abs(recon.drift), recon.unit);
  const sign = recon.drift > 0 ? "+" : recon.drift < 0 ? "−" : "";
  return (
    <div
      className="p-5 min-w-0"
      data-testid={`ledger-recon-${recon.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono truncate">
          {recon.label}
        </div>
        <div
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider ${
            balanced
              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
              : "bg-rose-500/10 text-rose-300 border-rose-500/30"
          }`}
          data-testid={`ledger-recon-${recon.id}-status`}
        >
          {balanced ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
          {balanced ? "Balanced" : "Drift"}
        </div>
      </div>

      <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">
        {recon.description}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <ReconSide
          label={recon.leftLabel}
          value={recon.leftValue}
          unit={recon.unit}
          tone="emerald"
        />
        <ReconSide
          label={recon.rightLabel}
          value={recon.rightValue}
          unit={recon.unit}
          tone="rose"
        />
      </div>

      <div
        className={`mt-3 rounded-lg border px-3 py-2 flex items-center justify-between text-xs ${
          balanced
            ? "border-emerald-500/20 bg-emerald-500/5"
            : "border-rose-500/30 bg-rose-500/10"
        }`}
        data-testid={`ledger-recon-${recon.id}-drift`}
      >
        <div className="text-slate-400 font-mono uppercase tracking-wider text-[10px]">
          Drift {recon.unit === "UNITS" ? "(units)" : "(₹)"}
        </div>
        <div
          className={`font-mono tabular-nums font-semibold ${
            balanced ? "text-emerald-300" : "text-rose-300"
          }`}
        >
          {sign}
          {driftFmt}
          <span className="ml-2 text-[10px] text-slate-500 font-normal">
            (tol ±{recon.unit === "UNITS" ? recon.tolerance : `₹${recon.tolerance}`})
          </span>
        </div>
      </div>

      {!balanced && (
        <div className="mt-2 flex justify-end">
          <DriftDrillButton recon={recon} />
        </div>
      )}

      {recon.note && (
        <div className="mt-2 text-[10px] text-slate-500 font-mono leading-relaxed">
          {recon.note}
        </div>
      )}
    </div>
  );
}

function ReconSide({ label, value, unit, tone }) {
  const toneCls =
    tone === "emerald"
      ? "border-emerald-500/20 bg-emerald-500/5"
      : "border-rose-500/20 bg-rose-500/5";
  return (
    <div className={`rounded-lg border ${toneCls} px-3 py-2 min-w-0`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono truncate">
        {label}
      </div>
      <div
        className="mt-1 text-sm md:text-base font-display text-white tabular-nums truncate"
        title={fmtReconValue(value, unit)}
      >
        {fmtReconValue(value, unit)}
      </div>
    </div>
  );
}

function AccountRow({ account, isAdmin, onAddEntry, onEdit, onDelete, onView, expanded }) {
  const side = account.side;
  const isBank = side === "bank";
  const isAsset = side === "asset";
  const isSystem = !!account.isSystem;
  const tone = isBank
    ? "text-amber-300 border-amber-500/30 bg-amber-500/10"
    : isAsset
    ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : "text-rose-400 border-rose-500/30 bg-rose-500/10";

  return (
    <div
      data-testid={`ledger-account-${account.id}`}
      className="bg-slate-900 border border-slate-800 rounded-xl"
    >
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${tone}`}>
            {isBank ? <Banknote size={15} /> : isAsset ? <Wallet size={15} /> : <Landmark size={15} />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate flex items-center gap-2">
              {account.name}
              {isSystem && (
                <span
                  title="System ledger — auto-managed"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border border-amber-500/30 bg-amber-500/10 text-amber-400 font-medium"
                >
                  <Shield size={9} /> System
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {account.category || "Uncategorized"}
              {account.notes ? <span className="text-slate-600"> · {account.notes}</span> : null}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Balance
            </div>
            <div className="text-base font-semibold text-white tabular-nums">
              {fmtINR(account.currentBalance, { decimals: 4 })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onView(account.id)}
            data-testid={`ledger-account-toggle-${account.id}`}
            className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-slate-800"
            title={expanded ? "Hide entries" : "Show entries"}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={() => onAddEntry(account)}
                data-testid={`ledger-account-add-entry-${account.id}`}
                className="text-emerald-400 hover:text-emerald-300 p-1.5 rounded-md hover:bg-slate-800"
                title="Add entry"
              >
                <Plus size={16} />
              </button>
              <button
                type="button"
                onClick={() => onEdit(account)}
                data-testid={`ledger-account-edit-${account.id}`}
                className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-slate-800"
                title={isSystem ? "Edit category / opening balance / notes" : "Edit account"}
              >
                <Pencil size={14} />
              </button>
              {!isSystem ? (
                <button
                  type="button"
                  onClick={() => onDelete(account)}
                  data-testid={`ledger-account-delete-${account.id}`}
                  className="text-rose-400 hover:text-rose-300 p-1.5 rounded-md hover:bg-slate-800"
                  title="Delete account"
                >
                  <Trash2 size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onDelete(account)}
                  data-testid={`ledger-account-delete-${account.id}`}
                  className="text-slate-500 hover:text-rose-400 p-1.5 rounded-md hover:bg-slate-800"
                  title="Delete system ledger (only allowed when empty — it will be auto-recreated next boot if still needed)"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EntriesTable({ entries, isAdmin, onDelete, onEdit, onExportCSV, onExportPDF, account }) {
  return (
    <div className="border border-slate-800 rounded-lg">
      {(onExportCSV || onExportPDF) && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-slate-900/60">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Entries · {entries.length}
          </div>
          <div className="flex items-center gap-1">
            {onExportCSV && (
              <button
                type="button"
                onClick={() => onExportCSV(account, entries)}
                data-testid={`ledger-account-export-csv-${account?.id}`}
                className="text-slate-400 hover:text-emerald-300 p-1 rounded hover:bg-slate-800 inline-flex items-center gap-1 text-[10px]"
                title="Export entries (CSV)"
              >
                <Download size={11} /> CSV
              </button>
            )}
            {onExportPDF && (
              <button
                type="button"
                onClick={() => onExportPDF(account, entries)}
                data-testid={`ledger-account-export-pdf-${account?.id}`}
                className="text-slate-400 hover:text-emerald-300 p-1 rounded hover:bg-slate-800 inline-flex items-center gap-1 text-[10px]"
                title="Export entries (PDF)"
              >
                <FileText size={11} /> PDF
              </button>
            )}
          </div>
        </div>
      )}
      {!entries.length ? (
        <div className="text-center text-xs text-slate-500 py-6">
          No entries yet for this account.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Description</th>
                {isAdmin && <th className="text-right px-3 py-2">&nbsp;</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const isDebit = e.type === "debit";
                return (
                  <tr
                    key={e.id}
                    data-testid={`ledger-entry-row-${e.id}`}
                    className="border-t border-slate-800 hover:bg-slate-900/50"
                  >
                    <td className="px-3 py-2 text-slate-300">{longDate(e.date)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border ${
                          isDebit
                            ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                            : "text-rose-400 border-rose-500/30 bg-rose-500/10"
                        }`}
                      >
                        {isDebit ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                        {isDebit ? "Debit" : "Credit"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-white">
                      {fmtINR(e.amount, { decimals: 4 })}
                    </td>
                    <td className="px-3 py-2 text-slate-400">
                      <div className="flex items-center gap-2">
                        {e.transferId && (
                          <span
                            title="Part of a transfer (deletes both legs)"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border border-sky-500/30 bg-sky-500/10 text-sky-400"
                          >
                            <ArrowLeftRight size={10} /> Transfer
                          </span>
                        )}
                        {e.journalId && (
                          <span
                            title="Part of a journal (deletes all linked legs)"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border border-violet-500/30 bg-violet-500/10 text-violet-400"
                          >
                            <Layers size={10} /> Journal
                          </span>
                        )}
                        {e.autoGenerated && (
                          <span
                            title="Auto-posted from a subscription/redemption transaction"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border border-amber-500/30 bg-amber-500/10 text-amber-400"
                          >
                            <Shield size={10} /> Auto
                          </span>
                        )}
                        <span className="truncate">{e.description || "—"}</span>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => onEdit(e)}
                          data-testid={`ledger-entry-edit-${e.id}`}
                          className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-800 mr-1"
                          title="Edit entry"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(e)}
                          data-testid={`ledger-entry-delete-${e.id}`}
                          className="text-rose-400 hover:text-rose-300 p-1 rounded hover:bg-slate-800"
                          title="Delete entry"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CategoryBreakdown({ title, items, accent }) {
  if (!items.length) return null;
  const colour =
    accent === "emerald"
      ? "text-emerald-400"
      : accent === "amber"
      ? "text-amber-300"
      : "text-rose-400";
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
        {title}
      </div>
      <div className="space-y-2">
        {items.map((g) => (
          <div
            key={g.category}
            className="flex items-center justify-between gap-3 text-sm"
          >
            <div className="text-slate-300 truncate">
              {g.category}
              <span className="text-slate-600 text-[10px] ml-1">({g.count})</span>
            </div>
            <div className={`tabular-nums font-medium ${colour}`}>
              {fmtINR(g.total, { decimals: 4 })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Ledger() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "admin";

  const [recon, setRecon] = useState(null);
  const [entriesByAccount, setEntriesByAccount] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Account dialog state
  const [accountDialog, setAccountDialog] = useState(null); // null | "new" | {account}
  const [accountForm, setAccountForm] = useState({
    name: "",
    side: "asset",
    category: "",
    openingBalance: "",
    notes: "",
  });

  // Entry dialog state
  const [entryDialog, setEntryDialog] = useState(null); // null | {account}
  const [entryForm, setEntryForm] = useState({
    type: "debit",
    amount: "",
    date: todayISO(),
    description: "",
  });

  const [deleteAccount, setDeleteAccount] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);

  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    date: todayISO(),
    description: "",
  });

  // Journal (multi-line) dialog state
  const [journalOpen, setJournalOpen] = useState(false);
  const [journalForm, setJournalForm] = useState({
    date: todayISO(),
    description: "",
    lines: [
      { accountId: "", type: "debit", amount: "", description: "" },
      { accountId: "", type: "credit", amount: "", description: "" },
    ],
  });

  // Edit entry dialog state
  const [editEntry, setEditEntry] = useState(null); // null | entry object
  const [editEntryForm, setEditEntryForm] = useState({
    date: "",
    type: "debit",
    amount: "",
    description: "",
  });

  // CSV bulk upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    accountId: "",
    file: null,
    fileName: "",
    replaceAll: false,
    rows: [],          // parsed preview rows
    parseErrors: [],   // client-side parse errors
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.ledgerReconciliation();
      setRecon(r);
    } catch (e) {
      toast({
        title: "Failed to load ledger",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEntries = async (accountId) => {
    try {
      const list = await api.ledgerEntries(accountId);
      setEntriesByAccount((m) => ({ ...m, [accountId]: list }));
    } catch (e) {
      toast({
        title: "Failed to load entries",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    }
  };

  const toggleExpand = async (accountId) => {
    const next = !expanded[accountId];
    setExpanded((m) => ({ ...m, [accountId]: next }));
    if (next && !entriesByAccount[accountId]) {
      await loadEntries(accountId);
    }
  };

  const openNewAccount = () => {
    setAccountForm({
      name: "",
      side: "asset",
      category: "",
      openingBalance: "",
      notes: "",
    });
    setAccountDialog("new");
  };

  const openEditAccount = (account) => {
    setAccountForm({
      name: account.name,
      side: account.side,
      category: account.category || "",
      openingBalance: String(account.openingBalance ?? 0),
      notes: account.notes || "",
    });
    setAccountDialog({ account });
  };

  const submitAccount = async () => {
    if (!accountForm.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      if (accountDialog === "new") {
        await api.createLedgerAccount({
          name: accountForm.name.trim(),
          side: accountForm.side,
          category: accountForm.category.trim(),
          openingBalance: Number(accountForm.openingBalance || 0),
          notes: accountForm.notes.trim(),
        });
        toast({ title: "Account created" });
      } else {
        // For system ledgers, omit name/side (server rejects changes there)
        const isSystem = !!accountDialog.account?.isSystem;
        const payload = {
          category: accountForm.category.trim(),
          openingBalance: Number(accountForm.openingBalance || 0),
          notes: accountForm.notes.trim(),
        };
        if (!isSystem) {
          payload.name = accountForm.name.trim();
          payload.side = accountForm.side;
        }
        await api.editLedgerAccount(accountDialog.account.id, payload);
        toast({ title: "Account updated" });
      }
      setAccountDialog(null);
      await load();
    } catch (e) {
      toast({
        title: "Failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const openAddEntry = (account) => {
    setEntryForm({
      type: account.side === "liability" ? "credit" : "debit", // asset & bank both default to debit (debit increases)
      amount: "",
      date: todayISO(),
      description: "",
    });
    setEntryDialog({ account });
  };

  const submitEntry = async () => {
    const amt = Number(entryForm.amount);
    if (!amt || amt <= 0) {
      toast({ title: "Amount must be > 0", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.createLedgerEntry({
        accountId: entryDialog.account.id,
        date: entryForm.date,
        type: entryForm.type,
        amount: amt,
        description: entryForm.description.trim(),
      });
      toast({ title: "Entry recorded" });
      setEntryDialog(null);
      await Promise.all([load(), loadEntries(entryDialog.account.id)]);
      setExpanded((m) => ({ ...m, [entryDialog.account.id]: true }));
    } catch (e) {
      toast({
        title: "Failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!deleteAccount) return;
    setBusy(true);
    try {
      await api.deleteLedgerAccount(deleteAccount.id);
      toast({ title: "Account deleted" });
      setDeleteAccount(null);
      setEntriesByAccount((m) => {
        const c = { ...m };
        delete c[deleteAccount.id];
        return c;
      });
      await load();
    } catch (e) {
      toast({
        title: "Failed to delete",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const [cleaningLedger, setCleaningLedger] = useState(false);
  const cleanupDuplicateLedgers = async () => {
    if (!window.confirm(
      "Scan for duplicate ledger accounts (same systemKey, same member-role, or same name) and orphan empty custom accounts? Duplicates will be merged into the entry with the most data. This cannot be undone."
    )) return;
    setCleaningLedger(true);
    try {
      const r = await api.cleanupDuplicateLedgerAccounts();
      const merged = r.merged || 0;
      const orphans = r.orphans_removed || 0;
      if (merged === 0 && orphans === 0) {
        toast({ title: "All clean", description: "No duplicate or orphan ledger accounts found." });
      } else {
        toast({
          title: "Cleanup complete",
          description: `Merged ${merged} duplicate account(s), removed ${orphans} orphan(s).`,
        });
      }
      await load();
    } catch (e) {
      toast({
        title: "Cleanup failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    }
    setCleaningLedger(false);
  };

  const confirmDeleteEntry = async () => {
    if (!deleteEntry) return;
    setBusy(true);
    try {
      const res = await api.deleteLedgerEntry(deleteEntry.id);
      toast({ title: res?.transferDeleted ? "Transfer deleted" : "Entry deleted" });
      const accId = deleteEntry.accountId;
      setDeleteEntry(null);
      await load();
      // Refresh all currently-expanded entries (a transfer may affect a different open account)
      const toReload = Object.keys(entriesByAccount);
      if (!toReload.includes(accId)) toReload.push(accId);
      await Promise.all(toReload.map((id) => loadEntries(id)));
    } catch (e) {
      toast({
        title: "Failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const openTransfer = () => {
    setTransferForm({
      fromAccountId: "",
      toAccountId: "",
      amount: "",
      date: todayISO(),
      description: "",
    });
    setTransferOpen(true);
  };

  const submitTransfer = async () => {
    if (!transferForm.fromAccountId || !transferForm.toAccountId) {
      toast({ title: "Pick both accounts", variant: "destructive" });
      return;
    }
    if (transferForm.fromAccountId === transferForm.toAccountId) {
      toast({ title: "Accounts must differ", variant: "destructive" });
      return;
    }
    const amt = Number(transferForm.amount);
    if (!amt || amt <= 0) {
      toast({ title: "Amount must be > 0", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.createLedgerTransfer({
        fromAccountId: transferForm.fromAccountId,
        toAccountId: transferForm.toAccountId,
        amount: amt,
        date: transferForm.date,
        description: transferForm.description.trim(),
      });
      toast({ title: "Transfer recorded" });
      setTransferOpen(false);
      // refresh affected accounts
      const affected = [transferForm.fromAccountId, transferForm.toAccountId];
      await load();
      await Promise.all(
        affected
          .filter((id) => entriesByAccount[id] !== undefined)
          .map((id) => loadEntries(id))
      );
    } catch (e) {
      toast({
        title: "Failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const openJournal = () => {
    setJournalForm({
      date: todayISO(),
      description: "",
      lines: [
        { accountId: "", type: "debit", amount: "", description: "" },
        { accountId: "", type: "credit", amount: "", description: "" },
      ],
    });
    setJournalOpen(true);
  };

  const updateJournalLine = (idx, field, value) => {
    setJournalForm((f) => {
      const lines = f.lines.slice();
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...f, lines };
    });
  };

  const addJournalLine = () => {
    setJournalForm((f) => ({
      ...f,
      lines: [...f.lines, { accountId: "", type: "debit", amount: "", description: "" }],
    }));
  };

  const removeJournalLine = (idx) => {
    setJournalForm((f) => {
      if (f.lines.length <= 2) return f;
      return { ...f, lines: f.lines.filter((_, i) => i !== idx) };
    });
  };

  const journalTotals = useMemo(() => {
    let dr = 0;
    let cr = 0;
    for (const ln of journalForm.lines) {
      const a = Number(ln.amount) || 0;
      if (ln.type === "debit") dr += a;
      else cr += a;
    }
    return {
      debits: dr,
      credits: cr,
      diff: Math.round((dr - cr) * 100) / 100,
      balanced: Math.round((dr - cr) * 100) === 0 && dr > 0,
    };
  }, [journalForm.lines]);

  const submitJournal = async () => {
    if (!journalTotals.balanced) {
      toast({
        title: "Journal does not balance",
        description: `Debits ${fmtINR(journalTotals.debits, { decimals: 4 })} ≠ Credits ${fmtINR(journalTotals.credits, { decimals: 4 })}`,
        variant: "destructive",
      });
      return;
    }
    const lines = journalForm.lines.map((l) => ({
      accountId: l.accountId,
      type: l.type,
      amount: Number(l.amount),
      description: l.description.trim(),
    }));
    if (lines.some((l) => !l.accountId)) {
      toast({ title: "Every line needs an account", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.createLedgerJournal({
        date: journalForm.date,
        description: journalForm.description.trim(),
        lines,
      });
      toast({ title: "Journal entry posted" });
      setJournalOpen(false);
      // refresh
      await load();
      await Promise.all(
        Object.keys(entriesByAccount).map((id) => loadEntries(id))
      );
    } catch (e) {
      toast({
        title: "Failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const openEditEntry = (entry) => {
    setEditEntryForm({
      date: entry.date,
      type: entry.type,
      amount: String(entry.amount ?? ""),
      description: entry.description || "",
    });
    setEditEntry(entry);
  };

  const submitEditEntry = async () => {
    if (!editEntry) return;
    const linked = !!(editEntry.transferId || editEntry.journalId);
    const payload = {
      date: editEntryForm.date,
      description: editEntryForm.description.trim(),
    };
    if (!linked) {
      const amt = Number(editEntryForm.amount);
      if (!amt || amt <= 0) {
        toast({ title: "Amount must be > 0", variant: "destructive" });
        return;
      }
      payload.type = editEntryForm.type;
      payload.amount = amt;
    }
    setBusy(true);
    try {
      await api.editLedgerEntry(editEntry.id, payload);
      toast({ title: "Entry updated" });
      const accId = editEntry.accountId;
      setEditEntry(null);
      await load();
      // refresh any expanded views (entry may be visible in multiple if linked)
      await Promise.all(
        Array.from(new Set([accId, ...Object.keys(entriesByAccount)])).map((id) =>
          loadEntries(id)
        )
      );
    } catch (e) {
      toast({
        title: "Failed",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  // ------------------ CSV bulk upload ------------------ //
  const openUpload = () => {
    setUploadForm({
      accountId: "",
      file: null,
      fileName: "",
      replaceAll: false,
      rows: [],
      parseErrors: [],
    });
    setUploadOpen(true);
  };

  const downloadSampleLedgerCSV = () => {
    const headers = ["Date", "Type", "Amount", "Description"];
    const rows = [
      ["2026-06-04", "credit", "25000", "Sample credit — money received"],
      ["2026-06-04", "debit", "1500", "Sample debit — fee paid"],
      ["04 Jun 2026", "credit", "10000.50", "Date can be YYYY-MM-DD or 'DD Mmm YYYY'"],
    ];
    downloadCSV("ledger_upload_sample.csv", headers, rows);
  };

  // Robust CSV parser supporting quoted fields with commas
  const parseCSV = (text) => {
    const rows = [];
    let cur = [];
    let buf = "";
    let inQuotes = false;
    const pushField = () => { cur.push(buf); buf = ""; };
    const pushRow = () => { rows.push(cur); cur = []; };
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { buf += '"'; i++; }
          else { inQuotes = false; }
        } else { buf += ch; }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ",") pushField();
        else if (ch === "\r") {/* ignore */}
        else if (ch === "\n") { pushField(); pushRow(); }
        else buf += ch;
      }
    }
    if (buf.length > 0 || cur.length > 0) { pushField(); pushRow(); }
    if (rows.length && rows[0].length && rows[0][0]) {
      rows[0][0] = rows[0][0].replace(/^\uFEFF/, "");
    }
    return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
  };

  const onUploadFileSelected = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const matrix = parseCSV(text);
      if (matrix.length < 2) {
        toast({
          title: "CSV is empty",
          description: "Need at least a header row and one data row.",
          variant: "destructive",
        });
        return;
      }
      const headers = matrix[0].map((h) => String(h).trim().toLowerCase());
      const idx = {
        date: headers.findIndex((h) => h === "date"),
        type: headers.findIndex((h) => h === "type" || h === "dr/cr" || h === "side"),
        amount: headers.findIndex((h) => h === "amount" || h === "value"),
        description: headers.findIndex((h) => h === "description" || h === "details" || h === "narration" || h === "note"),
      };
      const missing = [];
      if (idx.date < 0) missing.push("Date");
      if (idx.type < 0) missing.push("Type");
      if (idx.amount < 0) missing.push("Amount");
      if (missing.length) {
        toast({
          title: "Missing required columns",
          description: `CSV must include: ${missing.join(", ")}. Download the sample for the correct format.`,
          variant: "destructive",
        });
        return;
      }
      const rows = [];
      const errors = [];
      for (let i = 1; i < matrix.length; i++) {
        const row = matrix[i];
        const date = String(row[idx.date] ?? "").trim();
        const type = String(row[idx.type] ?? "").trim().toLowerCase();
        const rawAmt = String(row[idx.amount] ?? "").replace(/[,\s]/g, "").trim();
        const description = idx.description >= 0 ? String(row[idx.description] ?? "").trim() : "";
        if (!date) { errors.push(`Row ${i + 1}: missing Date`); continue; }
        if (type !== "debit" && type !== "credit") {
          errors.push(`Row ${i + 1}: Type must be 'debit' or 'credit' (got '${row[idx.type]}')`);
          continue;
        }
        const amount = Number(rawAmt);
        if (!amount || amount <= 0 || isNaN(amount)) {
          errors.push(`Row ${i + 1}: Amount must be > 0 (got '${row[idx.amount]}')`);
          continue;
        }
        rows.push({ date, type, amount, description });
      }
      setUploadForm((f) => ({
        ...f,
        file,
        fileName: file.name,
        rows,
        parseErrors: errors,
      }));
    } catch (e) {
      toast({
        title: "Could not read file",
        description: String(e?.message || e),
        variant: "destructive",
      });
    }
  };

  const submitUpload = async () => {
    if (!uploadForm.accountId) {
      toast({ title: "Choose target ledger", variant: "destructive" });
      return;
    }
    if (!uploadForm.rows.length) {
      toast({ title: "No valid rows to upload", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.bulkLedgerEntries({
        accountId: uploadForm.accountId,
        replaceAll: uploadForm.replaceAll,
        rows: uploadForm.rows,
      });
      toast({
        title: "Entries uploaded",
        description: `${r.inserted} entries added to ${r.accountName}${r.deletedExisting ? ` · ${r.deletedExisting} existing entries replaced` : ""}.`,
      });
      setUploadOpen(false);
      await load();
      if (entriesByAccount[r.accountId] !== undefined) {
        await loadEntries(r.accountId);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message || "Upload failed";
      const serverErrors = Array.isArray(detail?.errors) ? detail.errors : [];
      toast({
        title: "Upload rejected",
        description: serverErrors.length
          ? `${message} First issue: ${serverErrors[0]}`
          : message,
        variant: "destructive",
      });
      if (serverErrors.length) {
        setUploadForm((f) => ({ ...f, parseErrors: serverErrors }));
      }
    } finally {
      setBusy(false);
    }
  };



  // ------------------ Export helpers ------------------ //
  const entriesAcrossAll = async () => {
    // Fetch everything from server (don't rely on cached expanded entries)
    return api.ledgerEntries();
  };

  const exportEntriesCSV = (account, entries) => {
    const headers = ["Date", "Type", "Amount", "Description", "Transfer/Journal ID"];
    const rows = entries.map((e) => [
      e.date,
      e.type,
      e.amount,
      e.description || "",
      e.transferId || e.journalId || "",
    ]);
    const fname = account
      ? `ledger_${account.name.replace(/\s+/g, "_")}.csv`
      : "ledger_entries.csv";
    downloadCSV(fname, headers, rows);
  };

  const exportEntriesPDF = (account, entries) => {
    const isAccount = !!account;
    const totalDr = entries.filter((e) => e.type === "debit").reduce((s, e) => s + e.amount, 0);
    const totalCr = entries.filter((e) => e.type === "credit").reduce((s, e) => s + e.amount, 0);
    exportPDF({
      title: isAccount ? `Ledger · ${account.name}` : "All ledger entries",
      subtitle: isAccount
        ? `${account.side === "asset" ? "Asset" : account.side === "bank" ? "Bank" : "Liability"}${account.category ? " · " + account.category : ""}`
        : "Consolidated debit/credit log",
      filename: isAccount ? `ledger_${account.name.replace(/\s+/g, "_")}` : "ledger_entries",
      orientation: "landscape",
      summary: isAccount
        ? [
            ["Opening balance", fmtINR(account.openingBalance, { decimals: 4 })],
            ["Current balance", fmtINR(account.currentBalance, { decimals: 4 })],
            ["Entries", String(entries.length)],
          ]
        : [
            ["Total entries", String(entries.length)],
            ["Total debits", fmtINR(totalDr, { decimals: 4 })],
            ["Total credits", fmtINR(totalCr, { decimals: 4 })],
          ],
      columns: isAccount
        ? ["Date", "Type", "Amount", "Description", "Link"]
        : ["Date", "Account", "Side", "Type", "Amount", "Description"],
      rows: entries.map((e) =>
        isAccount
          ? [
              longDate(e.date),
              e.type,
              fmtINR(e.amount, { decimals: 4 }),
              e.description || "",
              e.transferId ? "Transfer" : e.journalId ? "Journal" : "",
            ]
          : [
              longDate(e.date),
              e.accountName || "",
              e.accountSide || "",
              e.type,
              fmtINR(e.amount, { decimals: 4 }),
              (e.transferId ? "[Transfer] " : e.journalId ? "[Journal] " : "") +
                (e.description || ""),
            ]
      ),
      foot: isAccount
        ? ["", "", "", "Balance:", fmtINR(account.currentBalance, { decimals: 4 })]
        : ["", "", "", "Totals", `Dr ${fmtINR(totalDr, { decimals: 4 })} / Cr ${fmtINR(totalCr, { decimals: 4 })}`, ""],
    });
  };

  const exportAllEntriesCSV = async () => {
    try {
      const all = await entriesAcrossAll();
      const headers = ["Date", "Account", "Side", "Type", "Amount", "Description", "Link"];
      const rows = all.map((e) => [
        e.date,
        e.accountName || "",
        e.accountSide || "",
        e.type,
        e.amount,
        e.description || "",
        e.transferId || e.journalId || "",
      ]);
      downloadCSV("ledger_all_entries.csv", headers, rows);
    } catch (e) {
      toast({ title: "Export failed", description: e?.message, variant: "destructive" });
    }
  };

  const exportAllEntriesPDF = async () => {
    try {
      const all = await entriesAcrossAll();
      exportEntriesPDF(null, all);
    } catch (e) {
      toast({ title: "Export failed", description: e?.message, variant: "destructive" });
    }
  };

  const exportBalanceSheetCSV = () => {
    const headers = ["Side", "Category", "Account", "Opening balance", "Current balance", "Notes"];
    const rows = [];
    for (const a of assets) {
      rows.push(["Asset", a.category || "Uncategorized", a.name, a.openingBalance, a.currentBalance, a.notes || ""]);
    }
    rows.push(["", "", "Total Assets", "", recon?.totalAssets ?? 0, ""]);
    for (const a of liabilities) {
      rows.push(["Liability", a.category || "Uncategorized", a.name, a.openingBalance, a.currentBalance, a.notes || ""]);
    }
    rows.push(["", "", "Total Liabilities", "", recon?.totalLiabilities ?? 0, ""]);
    rows.push(["", "", "Net Worth (A − L)", "", recon?.netWorth ?? 0, ""]);
    downloadCSV("balance_sheet.csv", headers, rows);
  };

  const exportBalanceSheetPDF = () => {
    const rows = [];
    rows.push([
      { content: "ASSETS", colSpan: 4, styles: { fontStyle: "bold", fillColor: [16, 185, 129], textColor: 255 } },
    ]);
    for (const a of assets) {
      rows.push([a.name, a.category || "—", fmtINR(a.openingBalance, { decimals: 4 }), fmtINR(a.currentBalance, { decimals: 4 })]);
    }
    rows.push([
      { content: "Total Assets", colSpan: 3, styles: { fontStyle: "bold" } },
      { content: fmtINR(recon?.totalAssets ?? 0, { decimals: 4 }), styles: { fontStyle: "bold" } },
    ]);
    rows.push([
      { content: "LIABILITIES", colSpan: 4, styles: { fontStyle: "bold", fillColor: [244, 63, 94], textColor: 255 } },
    ]);
    for (const a of liabilities) {
      rows.push([a.name, a.category || "—", fmtINR(a.openingBalance, { decimals: 4 }), fmtINR(a.currentBalance, { decimals: 4 })]);
    }
    rows.push([
      { content: "Total Liabilities", colSpan: 3, styles: { fontStyle: "bold" } },
      { content: fmtINR(recon?.totalLiabilities ?? 0, { decimals: 4 }), styles: { fontStyle: "bold" } },
    ]);
    rows.push([
      { content: "NET WORTH (Assets − Liabilities)", colSpan: 3, styles: { fontStyle: "bold", fillColor: [241, 245, 249] } },
      {
        content: fmtINR(recon?.netWorth ?? 0, { decimals: 4 }),
        styles: { fontStyle: "bold", textColor: (recon?.netWorth ?? 0) >= 0 ? [16, 185, 129] : [244, 63, 94] },
      },
    ]);
    exportPDF({
      title: "Balance Sheet",
      subtitle: "Snapshot of assets, liabilities and net worth",
      filename: "balance_sheet",
      orientation: "portrait",
      summary: [
        ["Total assets", fmtINR(recon?.totalAssets ?? 0, { decimals: 4 })],
        ["Total liabilities", fmtINR(recon?.totalLiabilities ?? 0, { decimals: 4 })],
        ["Net worth", fmtINR(recon?.netWorth ?? 0, { decimals: 4 })],
        ["Accounts", `${assets.length} assets · ${liabilities.length} liabilities`],
      ],
      columns: ["Account", "Category", "Opening", "Current"],
      rows,
    });
  };

  const [exportOpen, setExportOpen] = useState(false);

  const assets = recon?.assets || [];
  const liabilities = recon?.liabilities || [];
  const bank = recon?.bank || [];
  const netWorth = recon?.netWorth ?? 0;
  const netTone = useMemo(
    () => (netWorth >= 0 ? "text-emerald-400" : "text-rose-400"),
    [netWorth]
  );

  return (
    <AppShell>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            <BookOpen size={11} /> Ledger
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">
            Assets, Liabilities & Bank
          </h1>
          <p className="text-sm text-slate-400 mt-1 font-mono">
            Net worth = Assets − Liabilities − Bank. Bank is a separate
            top-level bucket for broker/bank float that doesn&apos;t inflate
            investment value.
          </p>
        </div>
        {isAdmin ? (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Button
                onClick={() => setExportOpen((o) => !o)}
                data-testid="ledger-export-btn"
                variant="outline"
                className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <Download size={14} className="mr-2" /> Export
                <ChevronDown size={12} className="ml-1.5" />
              </Button>
              {exportOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setExportOpen(false)}
                  />
                  <div
                    data-testid="ledger-export-menu"
                    className="absolute right-0 top-full mt-1 z-20 w-60 bg-slate-900 border border-slate-700 rounded-lg shadow-xl py-1"
                  >
                    <div className="px-3 py-1.5 text-[10px] text-slate-500 uppercase tracking-wider">
                      Balance sheet
                    </div>
                    <button
                      onClick={() => { setExportOpen(false); exportBalanceSheetCSV(); }}
                      data-testid="ledger-export-bs-csv"
                      className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                    >
                      <Download size={12} className="text-emerald-400" /> Balance sheet (CSV)
                    </button>
                    <button
                      onClick={() => { setExportOpen(false); exportBalanceSheetPDF(); }}
                      data-testid="ledger-export-bs-pdf"
                      className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                    >
                      <FileText size={12} className="text-emerald-400" /> Balance sheet (PDF)
                    </button>
                    <div className="border-t border-slate-800 my-1" />
                    <div className="px-3 py-1.5 text-[10px] text-slate-500 uppercase tracking-wider">
                      All entries
                    </div>
                    <button
                      onClick={() => { setExportOpen(false); exportAllEntriesCSV(); }}
                      data-testid="ledger-export-all-csv"
                      className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                    >
                      <Download size={12} className="text-sky-400" /> All entries (CSV)
                    </button>
                    <button
                      onClick={() => { setExportOpen(false); exportAllEntriesPDF(); }}
                      data-testid="ledger-export-all-pdf"
                      className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                    >
                      <FileText size={12} className="text-sky-400" /> All entries (PDF)
                    </button>
                    <div className="border-t border-slate-800 my-1" />
                    <div className="px-3 py-1.5 text-[10px] text-slate-500 uppercase tracking-wider">
                      Maintenance
                    </div>
                    <button
                      onClick={async () => {
                        setExportOpen(false);
                        try {
                          setBusy(true);
                          const r = await api.backfillAutoJournals();
                          toast({
                            title: "Auto-journals rebuilt",
                            description: `Posted ${r.posted} transactions${r.skipped ? `, ${r.skipped} skipped` : ""}.`,
                          });
                          await load();
                          await Promise.all(
                            Object.keys(entriesByAccount).map((id) => loadEntries(id))
                          );
                        } catch (e) {
                          toast({
                            title: "Backfill failed",
                            description: e?.response?.data?.detail || "Try again",
                            variant: "destructive",
                          });
                        } finally {
                          setBusy(false);
                        }
                      }}
                      data-testid="ledger-backfill-btn"
                      className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                    >
                      <Shield size={12} className="text-amber-400" /> Rebuild auto-journals from Cashflows
                    </button>
                    <button
                      onClick={async () => {
                        setExportOpen(false);
                        await cleanupDuplicateLedgers();
                      }}
                      disabled={cleaningLedger}
                      data-testid="ledger-cleanup-duplicates-btn"
                      className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50"
                    >
                      <Wand2 size={12} className="text-amber-400" />
                      {cleaningLedger ? "Cleaning…" : "Cleanup duplicate ledgers"}
                    </button>
                  </div>
                </>
              )}
            </div>
            <Button
              onClick={openJournal}
              data-testid="ledger-journal-btn"
              variant="outline"
              className="bg-slate-900 border-violet-500/40 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
            >
              <Layers size={14} className="mr-2" /> Journal
            </Button>
            <Button
              onClick={openTransfer}
              data-testid="ledger-transfer-btn"
              variant="outline"
              className="bg-slate-900 border-sky-500/40 text-sky-400 hover:bg-sky-500/10 hover:text-sky-300"
            >
              <ArrowLeftRight size={14} className="mr-2" /> Transfer
            </Button>
            <Button
              onClick={openUpload}
              data-testid="ledger-upload-csv-btn"
              variant="outline"
              className="bg-slate-900 border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
            >
              <Upload size={14} className="mr-2" /> Upload CSV
            </Button>
            <Button
              onClick={openNewAccount}
              data-testid="ledger-new-account-btn"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            >
              <Plus size={14} className="mr-2" /> New ledger
            </Button>
          </div>
        ) : (
          <div className="relative">
            <Button
              onClick={() => setExportOpen((o) => !o)}
              data-testid="ledger-export-btn"
              variant="outline"
              className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <FileText size={14} className="mr-2" /> Export PDF
              <ChevronDown size={12} className="ml-1.5" />
            </Button>
            {exportOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setExportOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-slate-900 border border-slate-700 rounded-lg shadow-xl py-1">
                  <button
                    onClick={() => { setExportOpen(false); exportBalanceSheetPDF(); }}
                    data-testid="ledger-export-bs-pdf"
                    className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                  >
                    <FileText size={12} className="text-emerald-400" /> Balance sheet (PDF)
                  </button>
                  <button
                    onClick={() => { setExportOpen(false); exportAllEntriesPDF(); }}
                    data-testid="ledger-export-all-pdf"
                    className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 flex items-center gap-2"
                  >
                    <FileText size={12} className="text-sky-400" /> All entries (PDF)
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Reconciliation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-5">
        <StatCard
          testId="ledger-total-assets"
          label="Total Assets"
          value={recon?.totalAssets ?? 0}
          accent="emerald"
          icon={Wallet}
        />
        <StatCard
          testId="ledger-total-liabilities"
          label="Total Liabilities"
          value={recon?.totalLiabilities ?? 0}
          accent="rose"
          icon={Landmark}
        />
        <StatCard
          testId="ledger-total-bank"
          label="Total Bank"
          value={recon?.totalBank ?? 0}
          accent="amber"
          icon={Banknote}
        />
        <div
          data-testid="ledger-net-worth"
          className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-4 md:p-5 min-w-0 overflow-hidden transition-transform hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono truncate">
              Net Worth (A − L − B)
            </div>
            <div className="shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center text-sky-400 border-sky-500/30 bg-sky-500/10">
              <Scale size={14} />
            </div>
          </div>
          <div
            className={`text-xl md:text-2xl lg:text-3xl font-display mt-3 tracking-tight tabular-nums whitespace-nowrap truncate ${netTone}`}
            title={fmtINR(netWorth, { decimals: 2 })}
          >
            {fmtINR(netWorth, { compact: true })}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 truncate tabular-nums font-mono">
            {fmtINR(netWorth, { decimals: 2 })}
          </div>
        </div>
      </div>

      {/* Assets ↔ Liabilities reconciliation panel */}
      {recon?.reconciliations?.length > 0 && (
        <ReconciliationPanel reconciliations={recon.reconciliations} allBalanced={recon.allBalanced} />
      )}

      {/* Category breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-4">
        <CategoryBreakdown
          title="Assets by category"
          items={recon?.assetsByCategory || []}
          accent="emerald"
        />
        <CategoryBreakdown
          title="Liabilities by category"
          items={recon?.liabilitiesByCategory || []}
          accent="rose"
        />
        <CategoryBreakdown
          title="Bank by category"
          items={recon?.bankByCategory || []}
          accent="amber"
        />
      </div>

      {/* Lists */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm mt-8">
          <Loader2 size={14} className="animate-spin" /> Loading ledger…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          {/* Assets column */}
          <section data-testid="ledger-assets-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
                  Assets
                </h2>
                <span className="text-[10px] text-slate-500">({assets.length})</span>
              </div>
              <div className="text-xs text-emerald-400 tabular-nums">
                {fmtINR(recon?.totalAssets ?? 0, { decimals: 4 })}
              </div>
            </div>
            {assets.length === 0 ? (
              <div className="border border-dashed border-slate-800 rounded-xl p-6 text-center text-sm text-slate-500">
                No asset accounts yet.
                {isAdmin && " Click 'New ledger' to create one."}
              </div>
            ) : (
              <div className="space-y-2">
                {assets.map((a) => (
                  <div key={a.id}>
                    <AccountRow
                      account={a}
                      isAdmin={isAdmin}
                      onAddEntry={openAddEntry}
                      onEdit={openEditAccount}
                      onDelete={setDeleteAccount}
                      onView={toggleExpand}
                      expanded={!!expanded[a.id]}
                    />
                    {expanded[a.id] && (
                      <div className="mt-2 mb-3 px-1">
                        <EntriesTable
                          account={a}
                          entries={entriesByAccount[a.id] || []}
                          isAdmin={isAdmin}
                          onDelete={setDeleteEntry}
                          onEdit={openEditEntry}
                          onExportCSV={exportEntriesCSV}
                          onExportPDF={exportEntriesPDF}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Liabilities column */}
          <section data-testid="ledger-liabilities-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-rose-400" />
                <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
                  Liabilities
                </h2>
                <span className="text-[10px] text-slate-500">({liabilities.length})</span>
              </div>
              <div className="text-xs text-rose-400 tabular-nums">
                {fmtINR(recon?.totalLiabilities ?? 0, { decimals: 4 })}
              </div>
            </div>
            {liabilities.length === 0 ? (
              <div className="border border-dashed border-slate-800 rounded-xl p-6 text-center text-sm text-slate-500">
                No liability accounts yet.
                {isAdmin && " Click 'New ledger' to create one."}
              </div>
            ) : (
              <div className="space-y-2">
                {liabilities.map((a) => (
                  <div key={a.id}>
                    <AccountRow
                      account={a}
                      isAdmin={isAdmin}
                      onAddEntry={openAddEntry}
                      onEdit={openEditAccount}
                      onDelete={setDeleteAccount}
                      onView={toggleExpand}
                      expanded={!!expanded[a.id]}
                    />
                    {expanded[a.id] && (
                      <div className="mt-2 mb-3 px-1">
                        <EntriesTable
                          account={a}
                          entries={entriesByAccount[a.id] || []}
                          isAdmin={isAdmin}
                          onDelete={setDeleteEntry}
                          onEdit={openEditEntry}
                          onExportCSV={exportEntriesCSV}
                          onExportPDF={exportEntriesPDF}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Bank column */}
          <section data-testid="ledger-bank-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
                  Bank
                </h2>
                <span className="text-[10px] text-slate-500">({bank.length})</span>
              </div>
              <div className="text-xs text-amber-300 tabular-nums">
                {fmtINR(recon?.totalBank ?? 0, { decimals: 4 })}
              </div>
            </div>
            {bank.length === 0 ? (
              <div className="border border-dashed border-slate-800 rounded-xl p-6 text-center text-sm text-slate-500">
                No bank accounts yet.
                {isAdmin && " Click 'New ledger' and pick side = Bank."}
              </div>
            ) : (
              <div className="space-y-2">
                {bank.map((a) => (
                  <div key={a.id}>
                    <AccountRow
                      account={a}
                      isAdmin={isAdmin}
                      onAddEntry={openAddEntry}
                      onEdit={openEditAccount}
                      onDelete={setDeleteAccount}
                      onView={toggleExpand}
                      expanded={!!expanded[a.id]}
                    />
                    {expanded[a.id] && (
                      <div className="mt-2 mb-3 px-1">
                        <EntriesTable
                          account={a}
                          entries={entriesByAccount[a.id] || []}
                          isAdmin={isAdmin}
                          onDelete={setDeleteEntry}
                          onEdit={openEditEntry}
                          onExportCSV={exportEntriesCSV}
                          onExportPDF={exportEntriesPDF}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Account Dialog */}
      <Dialog
        open={!!accountDialog}
        onOpenChange={(o) => !o && setAccountDialog(null)}
      >
        <DialogContent
          data-testid="ledger-account-dialog"
          className="bg-slate-900 border border-slate-800 text-slate-100 max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              {accountDialog === "new" ? "New ledger account" : "Edit ledger account"}
            </DialogTitle>
          </DialogHeader>
          {accountDialog?.account?.isSystem && (
            <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-2.5 py-1.5">
              System ledger — name and side are auto-managed. You can still edit category, opening balance and notes.
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Name</Label>
              <Input
                data-testid="ledger-form-name"
                value={accountForm.name}
                disabled={!!accountDialog?.account?.isSystem}
                onChange={(e) =>
                  setAccountForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. HDFC Savings, Home Loan"
                className="bg-slate-950 border-slate-800 text-white disabled:opacity-60"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Side</Label>
                <Select
                  value={accountForm.side}
                  disabled={!!accountDialog?.account?.isSystem}
                  onValueChange={(v) =>
                    setAccountForm((f) => ({ ...f, side: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="ledger-form-side"
                    className="bg-slate-950 border-slate-800 text-white disabled:opacity-60"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white">
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Category</Label>
                <Input
                  data-testid="ledger-form-category"
                  value={accountForm.category}
                  onChange={(e) =>
                    setAccountForm((f) => ({ ...f, category: e.target.value }))
                  }
                  placeholder="Bank, Property, Loan…"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">
                Opening balance (₹)
              </Label>
              <Input
                data-testid="ledger-form-opening"
                type="number"
                value={accountForm.openingBalance}
                onChange={(e) =>
                  setAccountForm((f) => ({
                    ...f,
                    openingBalance: e.target.value,
                  }))
                }
                placeholder="0"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Notes</Label>
              <Textarea
                data-testid="ledger-form-notes"
                value={accountForm.notes}
                onChange={(e) =>
                  setAccountForm((f) => ({ ...f, notes: e.target.value }))
                }
                placeholder="Optional"
                className="bg-slate-950 border-slate-800 text-white"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAccountDialog(null)}
              className="text-slate-400"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={submitAccount}
              disabled={busy}
              data-testid="ledger-form-save"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>Save</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry Dialog */}
      <Dialog open={!!entryDialog} onOpenChange={(o) => !o && setEntryDialog(null)}>
        <DialogContent
          data-testid="ledger-entry-dialog"
          className="bg-slate-900 border border-slate-800 text-slate-100 max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              Add entry · {entryDialog?.account?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Type</Label>
                <Select
                  value={entryForm.type}
                  onValueChange={(v) =>
                    setEntryForm((f) => ({ ...f, type: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="ledger-entry-type"
                    className="bg-slate-950 border-slate-800 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white">
                    <SelectItem value="debit">
                      Debit {entryDialog?.account?.side === "liability" ? "(↓ liability)" : entryDialog?.account?.side === "bank" ? "(↑ bank)" : "(↑ asset)"}
                    </SelectItem>
                    <SelectItem value="credit">
                      Credit {entryDialog?.account?.side === "liability" ? "(↑ liability)" : entryDialog?.account?.side === "bank" ? "(↓ bank)" : "(↓ asset)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Date</Label>
                <Input
                  data-testid="ledger-entry-date"
                  type="date"
                  value={entryForm.date}
                  onChange={(e) =>
                    setEntryForm((f) => ({ ...f, date: e.target.value }))
                  }
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Amount (₹)</Label>
              <Input
                data-testid="ledger-entry-amount"
                type="number"
                value={entryForm.amount}
                onChange={(e) =>
                  setEntryForm((f) => ({ ...f, amount: e.target.value }))
                }
                placeholder="0"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Description</Label>
              <Input
                data-testid="ledger-entry-description"
                value={entryForm.description}
                onChange={(e) =>
                  setEntryForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Optional note"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEntryDialog(null)}
              className="text-slate-400"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={submitEntry}
              disabled={busy}
              data-testid="ledger-entry-save"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>Record</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journal (multi-line) Dialog */}
      <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
        <DialogContent
          data-testid="ledger-journal-dialog"
          className="bg-slate-900 border border-slate-800 text-slate-100 max-w-3xl"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Layers size={16} className="text-violet-400" />
              Journal entry
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Post multiple debit/credit lines across accounts in one batch. Total debits must equal total credits.
          </p>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Date</Label>
                <Input
                  data-testid="ledger-journal-date"
                  type="date"
                  value={journalForm.date}
                  onChange={(e) =>
                    setJournalForm((f) => ({ ...f, date: e.target.value }))
                  }
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-400">Memo (optional)</Label>
                <Input
                  data-testid="ledger-journal-description"
                  value={journalForm.description}
                  onChange={(e) =>
                    setJournalForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="e.g. Equity contribution, rent settlement"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            <div className="border border-slate-800 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-900/80 text-slate-500 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-2">Account</th>
                    <th className="text-left px-2 py-2 w-28">Type</th>
                    <th className="text-right px-2 py-2 w-32">Amount</th>
                    <th className="text-left px-2 py-2">Line note</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {journalForm.lines.map((ln, idx) => (
                    <tr
                      key={idx}
                      data-testid={`ledger-journal-line-${idx}`}
                      className="border-t border-slate-800"
                    >
                      <td className="px-2 py-1.5">
                        <Select
                          value={ln.accountId}
                          onValueChange={(v) => updateJournalLine(idx, "accountId", v)}
                        >
                          <SelectTrigger
                            data-testid={`ledger-journal-line-${idx}-account`}
                            className="bg-slate-950 border-slate-800 text-white h-8"
                          >
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-60">
                            {[...assets, ...liabilities, ...bank].map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                <span className="flex items-center gap-2">
                                  <span
                                    className={`w-1.5 h-1.5 rounded-full ${
                                      a.side === "asset"
                                        ? "bg-emerald-400"
                                        : "bg-rose-400"
                                    }`}
                                  />
                                  {a.name}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5">
                        <Select
                          value={ln.type}
                          onValueChange={(v) => updateJournalLine(idx, "type", v)}
                        >
                          <SelectTrigger
                            data-testid={`ledger-journal-line-${idx}-type`}
                            className="bg-slate-950 border-slate-800 text-white h-8"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800 text-white">
                            <SelectItem value="debit">Debit</SelectItem>
                            <SelectItem value="credit">Credit</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          data-testid={`ledger-journal-line-${idx}-amount`}
                          type="number"
                          value={ln.amount}
                          onChange={(e) =>
                            updateJournalLine(idx, "amount", e.target.value)
                          }
                          placeholder="0"
                          className="bg-slate-950 border-slate-800 text-white h-8 text-right"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          data-testid={`ledger-journal-line-${idx}-note`}
                          value={ln.description}
                          onChange={(e) =>
                            updateJournalLine(idx, "description", e.target.value)
                          }
                          placeholder="Optional"
                          className="bg-slate-950 border-slate-800 text-white h-8"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={() => removeJournalLine(idx)}
                          disabled={journalForm.lines.length <= 2}
                          data-testid={`ledger-journal-line-${idx}-remove`}
                          className="text-slate-500 hover:text-rose-400 p-1 rounded disabled:opacity-30 disabled:hover:text-slate-500"
                          title="Remove line"
                        >
                          <X size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-800 bg-slate-950/60">
                    <td className="px-2 py-2 text-xs">
                      <button
                        type="button"
                        onClick={addJournalLine}
                        data-testid="ledger-journal-add-line"
                        className="text-violet-400 hover:text-violet-300 inline-flex items-center gap-1.5"
                      >
                        <Plus size={12} /> Add line
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right text-slate-500 text-[10px] uppercase tracking-wider">
                      Totals
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      <div className="text-emerald-400">Dr {fmtINR(journalTotals.debits, { decimals: 4 })}</div>
                      <div className="text-rose-400">Cr {fmtINR(journalTotals.credits, { decimals: 4 })}</div>
                    </td>
                    <td className="px-2 py-2 text-xs" colSpan={2}>
                      {journalTotals.balanced ? (
                        <span
                          data-testid="ledger-journal-balanced"
                          className="inline-flex items-center gap-1 text-emerald-400"
                        >
                          <Scale size={11} /> Balanced
                        </span>
                      ) : (
                        <span
                          data-testid="ledger-journal-imbalanced"
                          className="text-amber-400"
                        >
                          Imbalance: {fmtINR(journalTotals.diff, { decimals: 4 })}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setJournalOpen(false)}
              className="text-slate-400"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={submitJournal}
              disabled={busy || !journalTotals.balanced}
              data-testid="ledger-journal-save"
              className="bg-violet-500 hover:bg-violet-400 text-slate-950 disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Posting…
                </>
              ) : (
                <>Post journal</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent
          data-testid="ledger-edit-entry-dialog"
          className="bg-slate-900 border border-slate-800 text-slate-100 max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit entry · {editEntry?.accountName}
            </DialogTitle>
          </DialogHeader>
          {editEntry && (editEntry.transferId || editEntry.journalId) && (
            <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-2.5 py-1.5">
              Linked to a {editEntry.transferId ? "transfer" : "journal"} — only date and description can be changed. To change amount/type, delete the group and re-create.
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Date</Label>
                <Input
                  data-testid="ledger-edit-entry-date"
                  type="date"
                  value={editEntryForm.date}
                  onChange={(e) =>
                    setEditEntryForm((f) => ({ ...f, date: e.target.value }))
                  }
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Type</Label>
                <Select
                  value={editEntryForm.type}
                  disabled={!!(editEntry?.transferId || editEntry?.journalId)}
                  onValueChange={(v) =>
                    setEditEntryForm((f) => ({ ...f, type: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="ledger-edit-entry-type"
                    className="bg-slate-950 border-slate-800 text-white disabled:opacity-50"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white">
                    <SelectItem value="debit">Debit</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Amount (₹)</Label>
              <Input
                data-testid="ledger-edit-entry-amount"
                type="number"
                value={editEntryForm.amount}
                disabled={!!(editEntry?.transferId || editEntry?.journalId)}
                onChange={(e) =>
                  setEditEntryForm((f) => ({ ...f, amount: e.target.value }))
                }
                className="bg-slate-950 border-slate-800 text-white disabled:opacity-50"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Description</Label>
              <Input
                data-testid="ledger-edit-entry-description"
                value={editEntryForm.description}
                onChange={(e) =>
                  setEditEntryForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Optional"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditEntry(null)}
              className="text-slate-400"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={submitEditEntry}
              disabled={busy}
              data-testid="ledger-edit-entry-save"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>Save</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent
          data-testid="ledger-transfer-dialog"
          className="bg-slate-900 border border-slate-800 text-slate-100 max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowLeftRight size={16} className="text-sky-400" />
              Transfer between accounts
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Posts a credit on the source and a debit on the destination, linked as one transfer.
            Net worth stays constant.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">From account</Label>
              <Select
                value={transferForm.fromAccountId}
                onValueChange={(v) =>
                  setTransferForm((f) => ({ ...f, fromAccountId: v }))
                }
              >
                <SelectTrigger
                  data-testid="ledger-transfer-from"
                  className="bg-slate-950 border-slate-800 text-white"
                >
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-60">
                  {[...assets, ...liabilities, ...bank].map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            a.side === "asset" ? "bg-emerald-400" : a.side === "bank" ? "bg-amber-400" : "bg-rose-400"
                          }`}
                        />
                        {a.name}
                        <span className="text-[10px] text-slate-500">
                          · {fmtINR(a.currentBalance, { decimals: 4 })}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">To account</Label>
              <Select
                value={transferForm.toAccountId}
                onValueChange={(v) =>
                  setTransferForm((f) => ({ ...f, toAccountId: v }))
                }
              >
                <SelectTrigger
                  data-testid="ledger-transfer-to"
                  className="bg-slate-950 border-slate-800 text-white"
                >
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-60">
                  {[...assets, ...liabilities, ...bank]
                    .filter((a) => a.id !== transferForm.fromAccountId)
                    .map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              a.side === "asset" ? "bg-emerald-400" : a.side === "bank" ? "bg-amber-400" : "bg-rose-400"
                            }`}
                          />
                          {a.name}
                          <span className="text-[10px] text-slate-500">
                            · {fmtINR(a.currentBalance, { decimals: 4 })}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Amount (₹)</Label>
                <Input
                  data-testid="ledger-transfer-amount"
                  type="number"
                  value={transferForm.amount}
                  onChange={(e) =>
                    setTransferForm((f) => ({ ...f, amount: e.target.value }))
                  }
                  placeholder="0"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Date</Label>
                <Input
                  data-testid="ledger-transfer-date"
                  type="date"
                  value={transferForm.date}
                  onChange={(e) =>
                    setTransferForm((f) => ({ ...f, date: e.target.value }))
                  }
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Description</Label>
              <Input
                data-testid="ledger-transfer-description"
                value={transferForm.description}
                onChange={(e) =>
                  setTransferForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Optional note"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setTransferOpen(false)}
              className="text-slate-400"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={submitTransfer}
              disabled={busy}
              data-testid="ledger-transfer-save"
              className="bg-sky-500 hover:bg-sky-400 text-slate-950"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>Transfer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Bulk CSV upload dialog ----- */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent
          data-testid="ledger-upload-dialog"
          className="bg-slate-900 border border-slate-800 text-slate-100 max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload size={16} className="text-amber-400" />
              Upload ledger entries from CSV
            </DialogTitle>
            <p className="text-xs text-slate-400 mt-1">
              Required columns: <code className="text-amber-300">Date, Type, Amount</code>{" "}
              · Optional: <code className="text-amber-300">Description</code>.
              Type must be <code>debit</code> or <code>credit</code>. Dates can be{" "}
              <code>YYYY-MM-DD</code> or <code>04 Jun 2026</code>.
            </p>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sample download */}
            <button
              type="button"
              onClick={downloadSampleLedgerCSV}
              data-testid="ledger-upload-sample-btn"
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-950 border border-dashed border-slate-700 hover:border-amber-500/50 hover:bg-slate-900 text-xs text-slate-300"
            >
              <span className="flex items-center gap-2">
                <FileSpreadsheet size={14} className="text-amber-400" />
                Not sure about the format? Download <strong className="text-amber-300">ledger_upload_sample.csv</strong>
              </span>
              <Download size={12} className="text-slate-400" />
            </button>

            {/* Target ledger */}
            <div>
              <Label className="text-xs text-slate-400">Target ledger account</Label>
              <Select
                value={uploadForm.accountId}
                onValueChange={(v) =>
                  setUploadForm((f) => ({ ...f, accountId: v }))
                }
              >
                <SelectTrigger
                  data-testid="ledger-upload-account"
                  className="bg-slate-950 border-slate-800 text-white mt-1"
                >
                  <SelectValue placeholder="Choose the ledger to upload into" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-72">
                  {[...assets, ...liabilities, ...bank].map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            a.side === "asset" ? "bg-emerald-400" : a.side === "bank" ? "bg-amber-400" : "bg-rose-400"
                          }`}
                        />
                        {a.name}
                        <span className="text-[10px] text-slate-500">
                          · {a.side === "asset" ? "Asset" : a.side === "bank" ? "Bank" : "Liability"}
                          {a.category ? ` · ${a.category}` : ""}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File input */}
            <div>
              <Label className="text-xs text-slate-400">CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onUploadFileSelected(e.target.files?.[0])}
                data-testid="ledger-upload-file"
                className="bg-slate-950 border-slate-800 text-white mt-1 file:text-amber-300 file:bg-slate-800 file:border-0 file:rounded-md file:px-3 file:py-1 file:mr-3 hover:file:bg-slate-700"
              />
              {uploadForm.fileName && (
                <p className="text-[10px] text-slate-500 mt-1">
                  Selected: <span className="text-slate-300">{uploadForm.fileName}</span>
                </p>
              )}
            </div>

            {/* Preview */}
            {uploadForm.rows.length > 0 && (
              <div
                data-testid="ledger-upload-preview"
                className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-3"
              >
                <div className="flex items-center gap-2 text-xs text-emerald-300 mb-2">
                  <CheckCircle2 size={12} />
                  Ready to upload <strong>{uploadForm.rows.length}</strong> valid row
                  {uploadForm.rows.length === 1 ? "" : "s"}
                  {uploadForm.parseErrors.length > 0 && (
                    <span className="text-amber-400">
                      · {uploadForm.parseErrors.length} skipped
                    </span>
                  )}
                </div>
                <div className="max-h-32 overflow-y-auto text-[11px] font-mono">
                  {uploadForm.rows.slice(0, 5).map((r, i) => (
                    <div key={i} className="text-slate-400 truncate">
                      <span className="text-slate-500">{r.date}</span>{" "}
                      <span className={r.type === "debit" ? "text-emerald-300" : "text-rose-300"}>
                        {r.type.toUpperCase()}
                      </span>{" "}
                      <span className="text-white">{fmtINR(r.amount, { decimals: 4 })}</span>
                      {r.description ? <span className="text-slate-500"> · {r.description}</span> : null}
                    </div>
                  ))}
                  {uploadForm.rows.length > 5 && (
                    <div className="text-slate-600 italic">
                      … and {uploadForm.rows.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {uploadForm.parseErrors.length > 0 && (
              <div
                data-testid="ledger-upload-errors"
                className="border border-rose-500/30 bg-rose-500/5 rounded-lg p-3"
              >
                <div className="flex items-center gap-2 text-xs text-rose-300 mb-2">
                  <AlertCircle size={12} />
                  {uploadForm.parseErrors.length} row{uploadForm.parseErrors.length === 1 ? "" : "s"} skipped:
                </div>
                <div className="max-h-32 overflow-y-auto text-[11px] text-rose-200/80 space-y-0.5">
                  {uploadForm.parseErrors.slice(0, 10).map((e, i) => (
                    <div key={i}>{e}</div>
                  ))}
                  {uploadForm.parseErrors.length > 10 && (
                    <div className="text-rose-300/60 italic">
                      … and {uploadForm.parseErrors.length - 10} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Replace All toggle */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={uploadForm.replaceAll}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, replaceAll: e.target.checked }))
                }
                data-testid="ledger-upload-replace-all"
                className="mt-0.5 accent-amber-500"
              />
              <span className="text-xs text-slate-300">
                Replace all existing entries on this ledger
                <span className="block text-[10px] text-slate-500 mt-0.5">
                  Wipes existing standalone entries before inserting. Auto-posted
                  legs from subscriptions/transfers/journals are preserved.
                </span>
              </span>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setUploadOpen(false)}
              data-testid="ledger-upload-cancel"
              className="bg-slate-800 border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={submitUpload}
              disabled={busy || !uploadForm.accountId || uploadForm.rows.length === 0}
              data-testid="ledger-upload-submit"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload size={14} className="mr-2" /> Upload {uploadForm.rows.length || ""} entries
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete account confirm */}
      <AlertDialog
        open={!!deleteAccount}
        onOpenChange={(o) => !o && setDeleteAccount(null)}
      >
        <AlertDialogContent className="bg-slate-900 border border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Delete &ldquo;{deleteAccount?.name}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              All entries for this account will also be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAccount}
              data-testid="ledger-confirm-delete-account"
              className="bg-rose-500 hover:bg-rose-400 text-slate-950"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete entry confirm */}
      <AlertDialog
        open={!!deleteEntry}
        onOpenChange={(o) => !o && setDeleteEntry(null)}
      >
        <AlertDialogContent className="bg-slate-900 border border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {deleteEntry?.transferId
                ? "Delete transfer?"
                : deleteEntry?.journalId
                ? "Delete journal entry?"
                : "Delete entry?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {deleteEntry?.transferId
                ? `This will delete BOTH legs of the transfer (${fmtINR(
                    deleteEntry.amount,
                    { decimals: 4 }
                  )} on ${longDate(deleteEntry.date)}). The paired entry on the linked account will also be removed.`
                : deleteEntry?.journalId
                ? `This will delete ALL lines of the journal entry on ${longDate(
                    deleteEntry.date
                  )}. Every linked account will be updated.`
                : deleteEntry
                ? `${deleteEntry.type === "debit" ? "Debit" : "Credit"} ${fmtINR(
                    deleteEntry.amount,
                    { decimals: 4 }
                  )} on ${longDate(deleteEntry.date)} will be removed.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEntry}
              data-testid="ledger-confirm-delete-entry"
              className="bg-rose-500 hover:bg-rose-400 text-slate-950"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
